import { FC, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import { useToast } from '@/providers/Toast.provider';
import { Box, formattedCurrency, EftWithdrawTransactionResponse, Card, withStyles, capitalizeFirstLetter } from 'vopay';
import { TransactionCard, Title, DetailsRows, UtilButton, TransactionStatus, ExpandableText } from 'UI';
import useTransactionApproval from './useTransactionApproval';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type DataProps = {
  transactionId: string;
  activeModule: string;
  hasNewTransactionButton: boolean;
  accountId: string;
};

type TransactionInfoProps = {
  transaction: EftWithdrawTransactionResponse;
  Notes: string;
  isParent: boolean;
};

const EftWithdrawTransactionInfo: FC<TransactionInfoProps> = ({ transaction, Notes, isParent = false }) => {
  const history = useHistory();
  const toast = useToast();
  const { onSubNavClick } = useNavMenu();
  const { state } = useLocation<DataProps>();
  const { allApprovalTransactions } = useApprovalWorkflow();
  const { approvalHistory, fetchApprovalHistory } = useTransactionApproval();

  const isApprovalRequested = useMemo(() => {
    return allApprovalTransactions.some((t) => t.ID.toString() === transaction.TransactionID);
  }, [transaction.TransactionID, allApprovalTransactions]);

  useEffect(() => {
    if (!isApprovalRequested) return;

    (async () => {
      try {
        await fetchApprovalHistory({
          TransactionID: transaction.TransactionID,
        });
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch approval history' }));
      }
    })();
  }, [isApprovalRequested]);

  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.EftWithdrawTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  const recipientName =
    transaction.FirstName && transaction.LastName
      ? `${transaction.FirstName} ${transaction.LastName}`
      : transaction.CompanyName
        ? transaction.CompanyName
        : '-';

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.holdReleaseDate', { defaultValue: 'Hold Release Date' }),
      value: transaction.HoldReleaseDateTime,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: tGlobal('Transaction.TransactionHistory.transactionTypes.EFT Withdrawal', {
        defaultValue: 'EFT Withdrawal',
      }),
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.wireReferenceNumber', { defaultValue: 'Wire Reference Number' }),
      value: transaction.WireReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: Notes || '-',
    },
  ];

  const approvalHistoryDetails =
    approvalHistory.length > 0
      ? approvalHistory.flatMap((item) => [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: item.Username,
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: (
              <Box flx="col" gapSize="11px" ai="fe">
                <TransactionStatus
                  status={item.ApprovalDecision.toLowerCase()}
                  label={capitalizeFirstLetter(
                    tGlobal(`Transaction.TransactionHistory.statuses.${item.ApprovalDecision.toLowerCase()}`, {
                      defaultValue: item.ApprovalDecision,
                    }),
                  )}
                />
                <div>{item.DecisionDate}</div>
              </Box>
            ),
          },
          {
            label: t('ApprovalHistoryDetails.reason', { defaultValue: 'Reason' }),
            value: <ExpandableText text={item.Reason || ''} />,
            visible: item.Reason !== null,
          },
        ])
      : [
          {
            label: t('ApprovalHistoryDetails.approval', { defaultValue: 'Approval' }),
            value: '-',
          },
          {
            label: t('ApprovalHistoryDetails.decision', { defaultValue: 'Decision' }),
            value: '-',
          },
        ];

  const recipientDetails = [
    {
      label: t('AccountHolderDetails.recipientName', { defaultValue: 'Recipient Name' }),
      value:
        state && state.accountId && state.accountId !== '' ? (
          <UtilButton
            onClick={() => {
              if (clientAccountsEnabled) {
                history.push(`/client-accounts/${state.accountId}`);
                onSubNavClick(
                  'clientAccountsList',
                  `/client-accounts/${state.accountId}`,
                  t('AccountHolderDetails.clientAccountsList'),
                );
              } else {
                history.push(`/contacts/${state.accountId}`);
                onSubNavClick('contactAccounts', `/contacts/${state.accountId}`, t('AccountHolderDetails.contactList'));
              }
            }}
          >
            {recipientName}
          </UtilButton>
        ) : (
          recipientName
        ),
      visible: !!transaction.FirstName,
    },

    {
      label: t('AccountHolderDetails.address', { defaultValue: 'Address' }),
      value: transaction.Address1 || transaction.Address2 || '-',
    },
    {
      label: t('AccountHolderDetails.city', { defaultValue: 'City' }),
      value: transaction.City || '-',
    },
    {
      label: t('AccountHolderDetails.postalCode', { defaultValue: 'PostalCode / Zipcode' }),
      value: transaction.PostalCode || '-',
    },
    {
      label: t('AccountHolderDetails.country', { defaultValue: 'Country' }),
      value: transaction.Country || '-',
    },
    {
      label: t('AccountHolderDetails.primaryContact', { defaultValue: 'Primary Contact' }),
      value: transaction.PhoneNumber || '-',
    },
  ];

  const bankAccountDetails = [
    {
      label: t('BankAccountDetails.financialInstitutionNumber', { defaultValue: 'Financial Institution Number' }),
      value: transaction.FinancialInstitutionNumber || '-',
    },
    {
      label: t('BankAccountDetails.branchTransitNumber', { defaultValue: 'Branch Transit Number' }),
      value: transaction.BranchTransitNumber?.replaceAll('*', '•') || '-',
    },
    {
      label: t('BankAccountDetails.accountNumber', { defaultValue: 'Account Number' }),
      value: transaction.AccountNumber?.replaceAll('*', '•') || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.FailureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={recipientName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          status={transaction.TransactionStatus}
          type={'EFT Withdrawal'}
          transactionDate={transaction.TransactionDateTime}
          lastModified={transaction.LastModified}
          failureReason={transaction.FailureReason}
          isApprovalRequested={isApprovalRequested}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      {isApprovalRequested && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('ApprovalHistoryDetails.title', { defaultValue: 'Approval History' })}
          </Title>
          <DetailsRows className={cn('rows')} details={approvalHistoryDetails} />
        </Card>
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('recipientInformationTitle', { defaultValue: 'Recipient Information' })}
        </Title>
        <DetailsRows className={cn('rows')} details={recipientDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('bankAccountDetailsTitle', { defaultValue: 'Bank Account Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={bankAccountDetails} />
      </Card>

      {transaction.FailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default EftWithdrawTransactionInfo;
